import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Box,
  Avatar,
  Chip,
  LinearProgress,
  Grid,
  Alert,
  CircularProgress,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Science,
  TrendingUp,
  TrendingDown,
  Warning,
  CheckCircle,
  Refresh,
  AccessTime,
  Speed,
} from '@mui/icons-material';
import { apiService } from '../../services/api';
import { samplesService } from '../../services/samplesService';

interface LabMetrics {
  total_samples: number;
  avg_turnaround_time: number;
  completed_today: number;
  completion_rate: number;
  efficiency_score: number;
  samples_in_prep?: number;
  samples_in_analysis?: number;
  oldest_sample_hours?: number;
}

interface PerformanceLabsProps {
  tenantId?: string;
}

const PerformanceLabs: React.FC<PerformanceLabsProps> = ({ tenantId }) => {
  const [metrics, setMetrics] = useState<Record<string, LabMetrics>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdate, setLastUpdate] = useState<Date>(new Date());

  const fetchMetrics = async () => {
    try {
      setLoading(true);
      setError(null);

      // Get statistics for sample counts
      const stats = await samplesService.getStatistics();
      
      // Initialize laboratory metrics with ONLY sample counts from statistics
      // All other values (TAT, completion rates, etc.) will come from real-time backend data
      const laboratoryMetrics: Record<string, LabMetrics> = {
        SAA: {
          total_samples: (stats.par_statut?.LABORATOIRE_SAA_PREP_CHIMIQUE || 0) + 
                        (stats.par_statut?.LABORATOIRE_SAA_ANALYSE || 0),
          samples_in_prep: stats.par_statut?.LABORATOIRE_SAA_PREP_CHIMIQUE || 0,
          samples_in_analysis: stats.par_statut?.LABORATOIRE_SAA_ANALYSE || 0,
          avg_turnaround_time: 0, // Will be populated from real-time backend
          completed_today: 0, // Will be populated from real-time backend
          completion_rate: 0, // Will be populated from real-time backend
          efficiency_score: 0, // Will be populated from real-time backend
        },
        ICP: {
          total_samples: (stats.par_statut?.LABORATOIRE_ICP_PREP_CHIMIQUE || 0) + 
                        (stats.par_statut?.LABORATOIRE_ICP_ANALYSE || 0),
          samples_in_prep: stats.par_statut?.LABORATOIRE_ICP_PREP_CHIMIQUE || 0,
          samples_in_analysis: stats.par_statut?.LABORATOIRE_ICP_ANALYSE || 0,
          avg_turnaround_time: 0, // Will be populated from real-time backend
          completed_today: 0, // Will be populated from real-time backend
          completion_rate: 0, // Will be populated from real-time backend
          efficiency_score: 0, // Will be populated from real-time backend
        },
        AU: {
          total_samples: (stats.par_statut?.LABORATOIRE_AU_PREP_CHIMIQUE || 0) + 
                        (stats.par_statut?.LABORATOIRE_AU_ANALYSE || 0),
          samples_in_prep: stats.par_statut?.LABORATOIRE_AU_PREP_CHIMIQUE || 0,
          samples_in_analysis: stats.par_statut?.LABORATOIRE_AU_ANALYSE || 0,
          avg_turnaround_time: 0, // Will be populated from real-time backend
          completed_today: 0, // Will be populated from real-time backend
          completion_rate: 0, // Will be populated from real-time backend
          efficiency_score: 0, // Will be populated from real-time backend
        },
        VOLUMETRIE: {
          total_samples: (stats.par_statut?.LABORATOIRE_VOLUMETRIE_PREP_CHIMIQUE || 0) + 
                        (stats.par_statut?.LABORATOIRE_VOLUMETRIE_ANALYSE || 0),
          samples_in_prep: stats.par_statut?.LABORATOIRE_VOLUMETRIE_PREP_CHIMIQUE || 0,
          samples_in_analysis: stats.par_statut?.LABORATOIRE_VOLUMETRIE_ANALYSE || 0,
          avg_turnaround_time: 0, // Will be populated from real-time backend
          completed_today: 0, // Will be populated from real-time backend
          completion_rate: 0, // Will be populated from real-time backend
          efficiency_score: 0, // Will be populated from real-time backend
        },
      };

      // Get real-time metrics from backend - NO FALLBACK TO FAKE DATA!
      try {
        const realtimeData = await apiService.get('/analytics/realtime-metrics');
        console.log('🔍 Real-time metrics received:', realtimeData);
        
        if (realtimeData && typeof realtimeData === 'object') {
          // Use ONLY real-time data from backend
          Object.keys(realtimeData).forEach(lab => {
            if (laboratoryMetrics[lab]) {
              // Use real backend data, keep sample counts from statistics
              laboratoryMetrics[lab] = {
                ...laboratoryMetrics[lab], // Keep sample counts
                avg_turnaround_time: realtimeData[lab].avg_turnaround_time || 0,
                completed_today: realtimeData[lab].completed_today || 0,
                completion_rate: realtimeData[lab].completion_rate || 0,
                efficiency_score: realtimeData[lab].efficiency_score || 0,
              };
            }
          });
        }
      } catch (err) {
        console.error('❌ Failed to fetch real-time metrics:', err);
        setError('Impossible de récupérer les métriques en temps réel. Les données TAT ne sont pas disponibles.');
        
        // Set all TAT values to 0 if backend fails - NO FAKE DATA!
        Object.keys(laboratoryMetrics).forEach(lab => {
          laboratoryMetrics[lab].avg_turnaround_time = 0;
          laboratoryMetrics[lab].completed_today = 0;
          laboratoryMetrics[lab].completion_rate = 0;
          laboratoryMetrics[lab].efficiency_score = 0;
        });
      }

      setMetrics(laboratoryMetrics);
      setLastUpdate(new Date());
    } catch (err: any) {
      setError(err.message || 'Erreur lors du chargement des métriques');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMetrics();
    // Refresh every 30 seconds
    const interval = setInterval(fetchMetrics, 30000);
    return () => clearInterval(interval);
  }, []);

  const getStatusColor = (efficiency: number) => {
    if (efficiency >= 90) return 'success';
    if (efficiency >= 70) return 'warning';
    return 'error';
  };

  const getStatusLabel = (efficiency: number) => {
    if (efficiency >= 90) return 'Optimal';
    if (efficiency >= 70) return 'Moyen';
    return 'Attention';
  };

  const getTATColor = (tat: number) => {
    if (tat <= 24) return '#4caf50'; // Green
    if (tat <= 48) return '#ff9800'; // Orange
    return '#f44336'; // Red
  };

  if (loading && Object.keys(metrics).length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Summary Cards */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'primary.light', color: 'white' }}>
            <CardContent>
              <Typography variant="h6">Total en Cours</Typography>
              <Typography variant="h3">
                {Object.values(metrics).reduce((sum, lab) => sum + lab.total_samples, 0)}
              </Typography>
              <Typography variant="caption">Échantillons actifs</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'success.light', color: 'white' }}>
            <CardContent>
              <Typography variant="h6">TAT Moyen Global</Typography>
              <Typography variant="h3">
                {Object.values(metrics).length > 0
                  ? (() => {
                      const labsWithSamples = Object.values(metrics).filter(lab => lab.total_samples > 0);
                      const totalTAT = labsWithSamples.reduce((sum, lab) => sum + lab.avg_turnaround_time, 0);
                      return labsWithSamples.length > 0 ? Math.round((totalTAT / labsWithSamples.length) * 10) / 10 : 0;
                    })()
                  : 0}h
              </Typography>
              <Typography variant="caption">Temps de traitement</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'warning.light', color: 'white' }}>
            <CardContent>
              <Typography variant="h6">Efficacité Moyenne</Typography>
              <Typography variant="h3">
                {Object.values(metrics).length > 0
                  ? Math.round(
                      Object.values(metrics).reduce((sum, lab) => sum + lab.efficiency_score, 0) /
                      Object.values(metrics).length
                    )
                  : 0}%
              </Typography>
              <Typography variant="caption">Performance globale</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card sx={{ bgcolor: 'info.light', color: 'white' }}>
            <CardContent>
              <Typography variant="h6">Complétés Aujourd'hui</Typography>
              <Typography variant="h3">
                {Object.values(metrics).reduce((sum, lab) => sum + (lab.completed_today || 0), 0)}
              </Typography>
              <Typography variant="caption">Échantillons terminés</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Performance Table */}
      <Card>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">Performance des Laboratoires</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Dernière mise à jour: {lastUpdate.toLocaleTimeString('fr-FR')}
              </Typography>
              <IconButton size="small" onClick={fetchMetrics} disabled={loading}>
                <Refresh />
              </IconButton>
            </Box>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Laboratoire</TableCell>
                  <TableCell align="center">Échantillons</TableCell>
                  <TableCell align="center">Préparation</TableCell>
                  <TableCell align="center">Analyse</TableCell>
                  <TableCell align="center">TAT Moyen (h)</TableCell>
                  <TableCell align="center">Taux Completion</TableCell>
                  <TableCell align="center">Score Efficacité</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(metrics).map(([lab, data]) => (
                  <TableRow key={lab}>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Avatar sx={{ width: 32, height: 32, bgcolor: 'primary.main' }}>
                          <Science sx={{ fontSize: 16 }} />
                        </Avatar>
                        {lab}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip 
                        label={data.total_samples} 
                        size="small"
                        color={data.total_samples > 50 ? 'error' : data.total_samples > 20 ? 'warning' : 'default'}
                      />
                    </TableCell>
                    <TableCell align="center">
                      {data.samples_in_prep || 0}
                    </TableCell>
                    <TableCell align="center">
                      {data.samples_in_analysis || 0}
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={`Temps moyen de traitement: ${data.avg_turnaround_time}h`}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                          <AccessTime sx={{ fontSize: 16, color: getTATColor(data.avg_turnaround_time) }} />
                          <Typography sx={{ color: getTATColor(data.avg_turnaround_time), fontWeight: 'bold' }}>
                            {data.avg_turnaround_time}
                          </Typography>
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                        <LinearProgress 
                          variant="determinate" 
                          value={data.completion_rate} 
                          sx={{ width: 60, height: 6, borderRadius: 3 }}
                          color={data.completion_rate > 90 ? 'success' : data.completion_rate > 70 ? 'warning' : 'error'}
                        />
                        <Typography variant="body2">{Math.round(data.completion_rate)}%</Typography>
                        {data.completion_rate > 90 ? (
                          <TrendingUp color="success" sx={{ fontSize: 16 }} />
                        ) : (
                          <TrendingDown color="error" sx={{ fontSize: 16 }} />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={<Speed sx={{ fontSize: 16 }} />}
                        label={`${Math.round(data.efficiency_score)}%`}
                        color={getStatusColor(data.efficiency_score)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        icon={data.efficiency_score >= 90 ? <CheckCircle /> : <Warning />}
                        label={getStatusLabel(data.efficiency_score)}
                        color={getStatusColor(data.efficiency_score)}
                        size="small"
                      />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          {/* Legend */}
          <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="subtitle2" gutterBottom>Légende:</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, bgcolor: '#4caf50', borderRadius: '50%' }} />
                  <Typography variant="caption">TAT ≤ 24h: Excellent</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, bgcolor: '#ff9800', borderRadius: '50%' }} />
                  <Typography variant="caption">TAT 24-48h: Acceptable</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Box sx={{ width: 12, height: 12, bgcolor: '#f44336', borderRadius: '50%' }} />
                  <Typography variant="caption">TAT &gt; 48h: Critique</Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CheckCircle sx={{ fontSize: 16, color: 'success.main' }} />
                  <Typography variant="caption">Efficacité ≥ 90%: Optimal</Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default PerformanceLabs;
